/**
 * Which role a badge is printed for, when a person holds more than one.
 *
 * One badge per person, not one per role — a chair who also has an accepted
 * paper gets a single badge labeled "Chair", not two badges to sort out at
 * check-in. Priority is fixed rather than configurable: it's a rare edge
 * case, and a dropdown for it would be more UI than the problem deserves.
 *
 * 'reviewer' prints as "Guest" by design choice — the underlying
 * conference_roles value stays 'reviewer' everywhere else in the app; this
 * is purely a display label for the badge.
 */
const ROLE_PRIORITY = ['chair', 'organizer', 'reviewer', 'author'];

const ROLE_LABELS = {
  chair: 'Chair',
  organizer: 'Organizer',
  reviewer: 'Guest',
  author: 'Author',
};

/**
 * @param {string[]} conferenceRoles - roles from conference_roles for this
 *   person in this conference, e.g. ['chair'] or ['reviewer']
 * @param {boolean} hasAcceptedSubmission - true if they have at least one
 *   accepted paper in this conference
 * @returns {string|null} the raw role key to badge them as, or null if
 *   they're not eligible for a badge at all
 */
export function resolveBadgeRole(conferenceRoles, hasAcceptedSubmission) {
  const roles = new Set(conferenceRoles || []);
  if (hasAcceptedSubmission) roles.add('author');
  return ROLE_PRIORITY.find((r) => roles.has(r)) || null;
}

export function isBadgeEligible(conferenceRoles, hasAcceptedSubmission) {
  return resolveBadgeRole(conferenceRoles, hasAcceptedSubmission) !== null;
}

export function badgeRoleLabel(role) {
  return ROLE_LABELS[role] || (role ? role.charAt(0).toUpperCase() + role.slice(1) : '');
}
